import React from 'react';
import { Warning, ArrowsClockwise, ArrowLeft } from '@phosphor-icons/react';

/**
 * Global error boundary.
 *
 * Without this, any JS exception inside an admin page (malformed data,
 * missing field after a deploy, etc.) makes React unmount the whole subtree
 * and the user ends up on a **blank page with no explanation**.
 *
 * This boundary captures the error, displays a recoverable UI with:
 *   - the error message (for the admin to report back)
 *   - a "Retry" button that remounts children
 *   - a "Hard reload" hint covering stale-bundle cases after a deploy
 *
 * Usage: wrap routes or layouts with `<ErrorBoundary label="admin">`.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console so devtools / Sentry pick it up
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label || 'app', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRetry = () => {
    this.setState(prev => ({ hasError: false, error: null, errorInfo: null, retryKey: prev.retryKey + 1 }));
  };

  handleHardReload = () => {
    // Force full reload bypassing cache
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    }
  };

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const msg = err?.message || String(err) || 'Erreur inconnue';
      const stack = err?.stack || '';
      const url = typeof window !== 'undefined' ? window.location.pathname : '';

      return (
        <div className="min-h-[60vh] flex items-start justify-center p-6 md:p-10" data-testid="error-boundary">
          <div className="max-w-2xl w-full bg-white border border-amber-200 shadow-card">
            <div className="flex items-start gap-4 p-6 md:p-8 border-b border-amber-100 bg-amber-50/60">
              <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-amber-100 text-amber-700 rounded-sm">
                <Warning size={20} weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700 mb-1">
                  Page indisponible
                </p>
                <h2 className="font-heading text-xl md:text-2xl text-charcoal leading-tight mb-2">
                  Nous n&apos;avons pas pu afficher cet écran.
                </h2>
                <p className="text-sm text-graphite/80 leading-relaxed">
                  Une erreur s&apos;est produite pendant le rendu. Essayez de recharger la page —
                  si le problème persiste après un rechargement complet (Ctrl + Shift + R),
                  transmettez le message ci-dessous à l&apos;équipe technique.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 space-y-5">
              <div className="bg-stone/10 border border-stone/40 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-graphite/50 mb-1">
                  Message technique
                </p>
                <p className="font-mono text-xs text-charcoal break-words" data-testid="error-boundary-message">
                  {msg}
                </p>
                {url && (
                  <p className="font-mono text-[11px] text-graphite/50 mt-2 break-all">Page : {url}</p>
                )}
              </div>

              {stack && (
                <details className="text-xs text-graphite/80">
                  <summary className="cursor-pointer hover:text-charcoal">Détails techniques</summary>
                  <pre className="mt-2 p-3 bg-stone/10 border border-stone/40 overflow-auto max-h-64 whitespace-pre-wrap font-mono text-[10px]">
                    {stack}
                  </pre>
                </details>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <button
                  onClick={this.handleRetry}
                  className="btn-primary text-sm flex items-center gap-1.5"
                  data-testid="error-boundary-retry"
                >
                  <ArrowsClockwise size={14} weight="bold" /> Réessayer
                </button>
                <button
                  onClick={this.handleHardReload}
                  className="btn-secondary text-sm flex items-center gap-1.5"
                  data-testid="error-boundary-reload"
                >
                  <ArrowsClockwise size={14} /> Recharger entièrement
                </button>
                <button
                  onClick={this.handleBack}
                  className="text-xs text-graphite/80 hover:text-charcoal flex items-center gap-1.5 ml-auto"
                  data-testid="error-boundary-back"
                >
                  <ArrowLeft size={12} /> Page précédente
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // When retrying, use `key` to remount the subtree and re-run data fetches
    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}
